// "Stops & closeout" tab: every stop across the daily cycles, scope-change reports, the
// PH cancellation, the RS closeout and the archive. Read-only: it shows what the server
// recorded and never decides whether work may proceed (the eligibility panel does).
import { Link } from 'react-router-dom'
import { RuleRefs, SignatureLine } from '@/components/evidence'
import { Badge, Banner, Card, cx, EmptyState, KeyValue, SectionTitle } from '@/components/ui'
import { AA_CLOSEOUT_CHECKS, ISOLATION_DISPOSITION_LABELS, PH_CANCELLATION_DECLARATIONS } from '@/domain/catalogue'
import type { PermitBundle } from '@/domain/types'
import type { PermitViewProps } from '@/features/contracts'
import { fmtDateTime } from '@/lib/format'
import { ArchiveCard } from './ArchiveSection'
import {
  archiveStatusText,
  cancellationReasonLabel,
  currentCycle,
  cycleStateText,
  endReasonLabel,
  humanize,
  newestFirst,
  personText,
  phWorkStatus,
  roleLabel,
  stopsByCycle,
  SUSPENSION_STAGE_TEXT,
  suspensionStage,
} from './logic'
import { CheckRecord, YesNoBadge } from './parts'
import { SuspensionRecord } from './StopRecord'

function isolationText(code: string, note?: string | null): string {
  const label = ISOLATION_DISPOSITION_LABELS[code] ?? humanize(code)
  return note ? `${label} - ${note}` : label
}

function signedText(a: { signer: { display_name: string }; server_signed_at: string }): string {
  return `Signed by ${a.signer.display_name}, ${fmtDateTime(a.server_signed_at)}`
}

/** Plain statements of the recorded state; the eligibility panel above remains the authority. */
function StatusNotes({ bundle }: { bundle: PermitBundle }) {
  const notes: { key: string; tone: 'bad' | 'warn' | 'info' | 'ok'; title: string; body: string }[] = []
  const state = bundle.permit.lifecycle_state
  const cycle = currentCycle(bundle)

  if (state === 'authorised' && cycle?.cycle_state === 'suspension_reported') {
    const newest = [...cycle.suspensions].sort((a, b) => newestFirst(a.server_received_at, b.server_received_at))[0]
    notes.push({
      key: 'stop',
      tone: 'bad',
      title: `Work stopped - daily cycle #${cycle.cycle_number}`,
      body: newest
        ? `A stop was reported. ${SUSPENSION_STAGE_TEXT[suspensionStage(newest)].label}. Work must not restart under this cycle.`
        : 'A stop was reported. Work must not restart under this cycle.',
    })
  }
  if (state === 'authorised' && cycle?.cycle_state === 'suspended') {
    notes.push({
      key: 'suspended',
      tone: 'warn',
      title: `Daily cycle #${cycle.cycle_number} is suspended`,
      body: 'Work can restart only with a new daily cycle that is validated again.',
    })
  }
  if (state === 'authorised' && bundle.scope_changes.some((r) => r.revision_id === bundle.permit.current_revision_id)) {
    notes.push({
      key: 'scope',
      tone: 'bad',
      title: 'Scope change reported - work stopped',
      body: 'The work differs from this permit. The Permit Holder cancels this permit and the changed work needs a new permit.',
    })
  }
  if (state === 'cancellation_signed') {
    notes.push({
      key: 'cancelled',
      tone: 'warn',
      title: 'Cancelled by the Permit Holder',
      body: 'No more work under this permit. Waiting for the Responsible Supervisor to inspect the site and close out.',
    })
  }
  if (state === 'closed_out') {
    notes.push({
      key: 'closed',
      tone: 'info',
      title: `Closed out by the ${roleLabel(bundle.closeout?.attestation.signing_role ?? 'RS')}`,
      body: 'No more work under this permit. The archive copy is made separately and cannot reopen the permit.',
    })
  }
  if (state === 'archived') {
    notes.push({ key: 'archived', tone: 'info', title: 'Archived', body: 'The closed record is stored with its checksums.' })
  }
  if (notes.length === 0) return null
  return (
    <div className="space-y-2">
      {notes.map((n) => (
        <Banner key={n.key} tone={n.tone} title={n.title}>
          {n.body}
        </Banner>
      ))}
    </div>
  )
}

function CloseSteps({ bundle }: { bundle: PermitBundle }) {
  const c = bundle.cancellation
  const co = bundle.closeout
  const job = bundle.archive?.job ?? null
  const steps = [
    {
      title: 'PH cancels the permit',
      done: Boolean(c),
      status: c ? signedText(c.attestation) : 'Not signed',
    },
    {
      title: 'RS inspects and closes out',
      done: Boolean(co),
      status: co ? signedText(co.attestation) : c ? 'Waiting for the Responsible Supervisor' : 'After the PH cancels',
    },
    {
      title: 'Archive copy',
      done: job?.status === 'succeeded',
      status: job ? archiveStatusText(job.status).label : co ? 'Waiting for the archive job' : 'After the RS closes out',
    },
  ]
  return (
    <Card>
      <SectionTitle>Closing this permit</SectionTitle>
      <ol className="grid gap-2 sm:grid-cols-3">
        {steps.map((s, i) => (
          <li key={s.title} className={cx('rounded-lg border p-3', s.done ? 'border-ok/40 bg-ok-light' : 'border-line bg-white')}>
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
              Step {i + 1} - {s.done ? 'Done' : 'Not done'}
            </p>
            <p className="font-semibold text-ink">{s.title}</p>
            <p className="text-sm text-ink-soft">{s.status}</p>
          </li>
        ))}
      </ol>
    </Card>
  )
}

function StopsCard({ bundle }: { bundle: PermitBundle }) {
  const groups = stopsByCycle(bundle)
  return (
    <Card>
      <SectionTitle aside={<RuleRefs ruleIds={['CORE-062', 'CORE-063']} />}>Stops and suspensions</SectionTitle>
      {groups.length === 0 ? (
        <EmptyState title="No stops reported on this permit">
          Anyone who can see the permit can report a stop. The PH declares the site safe and the RS confirms.
        </EmptyState>
      ) : (
        <div className="space-y-4">
          {groups.map(({ cycle, suspensions }) => {
            const st = cycleStateText(cycle.cycle_state)
            const ended = endReasonLabel(cycle.end_reason)
            return (
              <section key={cycle.id} aria-label={`Daily cycle ${cycle.cycle_number}`}>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-ink">Daily cycle #{cycle.cycle_number}</h3>
                  <span className="text-sm text-ink-soft">State now:</span>
                  <Badge tone={st.tone}>{st.label}</Badge>
                  {ended ? (
                    <span className="text-sm text-ink-soft">
                      Ended: {ended}
                      {cycle.ended_at ? `, ${fmtDateTime(cycle.ended_at)}` : ''}
                    </span>
                  ) : null}
                </div>
                <div className="space-y-2">
                  {suspensions.map((s) => (
                    <SuspensionRecord key={s.id} s={s} cycle={cycle} />
                  ))}
                </div>
              </section>
            )
          })}
          <p className="text-xs text-ink-soft">Work restarts only with a new daily cycle (see Daily cycles).</p>
        </div>
      )}
    </Card>
  )
}

function ScopeChangesCard({ bundle }: { bundle: PermitBundle }) {
  const reports = [...bundle.scope_changes].sort((a, b) => newestFirst(a.server_received_at, b.server_received_at))
  return (
    <Card>
      <SectionTitle aside={<RuleRefs ruleIds={['CORE-027', 'CORE-070']} />}>Scope changes</SectionTitle>
      {reports.length === 0 ? (
        <EmptyState title="No scope changes reported" />
      ) : (
        <ul className="space-y-2">
          {reports.map((r) => (
            <li key={r.id} className="rounded-lg border border-line bg-white p-3">
              <p className="whitespace-pre-wrap text-sm text-ink">{r.description}</p>
              <div className="mt-2">
                <KeyValue
                  items={[
                    ['Reported by', `${personText(r.reporter)} as ${roleLabel(r.reporter_role)}`],
                    ['Received by the server', fmtDateTime(r.server_received_at)],
                    [
                      'New permit',
                      r.replacement_permit_id ? (
                        <Link to={`/permits/${r.replacement_permit_id}`} className="tap inline-flex items-center font-semibold text-brand-dark underline">
                          Open the new permit
                        </Link>
                      ) : (
                        'None linked yet'
                      ),
                    ],
                  ]}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

function CancellationCard({ bundle }: { bundle: PermitBundle }) {
  const c = bundle.cancellation
  return (
    <Card>
      <SectionTitle aside={<RuleRefs ruleIds={['CORE-070', 'CORE-071']} />}>PH cancellation</SectionTitle>
      {!c ? (
        <EmptyState title="Not cancelled">Only the nominated Permit Holder can cancel. Cancelling stops all work at once.</EmptyState>
      ) : (
        <div className="space-y-3">
          <Badge tone={c.work_complete ? 'ok' : 'warn'}>{c.work_complete ? 'Work complete' : 'Work incomplete'}</Badge>
          <KeyValue
            cols={1}
            items={[
              ['Reason', cancellationReasonLabel(c.reason_code)],
              ['Work', c.work_complete ? 'Complete' : 'Incomplete'],
              ['Worksite condition', <span className="whitespace-pre-wrap">{c.worksite_condition}</span>],
              ['Tools and equipment removed', <YesNoBadge value={c.tools_equipment_removed} />],
              ['Work continues on', c.continuation_reference ?? (c.work_complete ? 'Not applicable (work complete)' : 'Not stated')],
              ['Isolations', isolationText(c.isolation_disposition)],
            ]}
          />
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-faint">PH declarations</p>
            <CheckRecord options={PH_CANCELLATION_DECLARATIONS} values={c.declarations} />
          </div>
          <SignatureLine a={c.attestation} label="Permit Holder cancellation" />
        </div>
      )}
    </Card>
  )
}

function CloseoutCard({ bundle }: { bundle: PermitBundle }) {
  const co = bundle.closeout
  const phStatus = phWorkStatus(bundle.cancellation)
  return (
    <Card>
      <SectionTitle aside={<RuleRefs ruleIds={['CORE-072']} />}>RS closeout</SectionTitle>
      {!co ? (
        <EmptyState title="Not closed out">
          {bundle.cancellation
            ? 'Waiting for the Responsible Supervisor to inspect the worksite and close out.'
            : 'The Responsible Supervisor closes out after the Permit Holder cancels.'}
        </EmptyState>
      ) : (
        <div className="space-y-3">
          <KeyValue
            cols={1}
            items={[
              ['Site safe', <YesNoBadge value={co.site_safe_confirmed} judge />],
              [
                'Work status confirmed',
                `${co.work_status_confirmed === 'complete' ? 'Complete' : 'Incomplete'}${phStatus ? ` (PH stated: ${phStatus})` : ''}`,
              ],
              ['Inspection findings', <span className="whitespace-pre-wrap">{co.inspection_findings}</span>],
              ['Operation check', co.operation_check ? <span className="whitespace-pre-wrap">{co.operation_check}</span> : 'None recorded'],
              ['Isolations', isolationText(co.isolation_disposition, co.isolation_disposition_note)],
            ]}
          />
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-faint">Closeout checks</p>
            <CheckRecord options={AA_CLOSEOUT_CHECKS} values={co.checks} />
          </div>
          <SignatureLine a={co.attestation} label={`${roleLabel(co.attestation.signing_role)} closeout`} />
        </div>
      )}
    </Card>
  )
}

export function CloseoutView({ bundle }: PermitViewProps) {
  return (
    <div className="space-y-4">
      <StatusNotes bundle={bundle} />
      <CloseSteps bundle={bundle} />
      <StopsCard bundle={bundle} />
      <ScopeChangesCard bundle={bundle} />
      <div className="grid gap-4 lg:grid-cols-2">
        <CancellationCard bundle={bundle} />
        <CloseoutCard bundle={bundle} />
      </div>
      <ArchiveCard bundle={bundle} />
    </div>
  )
}
